import * as React from 'react';
import { useEditor } from '@grapesjs/react';
import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  Close,
  Delete,
  Add,
} from '@mui/icons-material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import { BTN_CLS, ROUND_BORDER_COLOR, cx } from './common';

const inputBaseProps = {
  sx: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '12px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
      },
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.1)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.2)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#3b82f6',
      },
    },
  },
};

export default function StylePropertyField({
  prop,
  ...rest
}) {
  const editor = useEditor();
  const handleChange = (value) => {
    prop.upValue(value);
  };

  const onChange = (ev) => {
    handleChange(ev.target.value);
  };

  const openAssets = () => {
    const { Assets } = editor;
    Assets.open({
      select: (asset, complete) => {
        prop.upValue(asset.getSrc(), { partial: !complete });
        complete && Assets.close();
      },
      types: ['image'],
      accept: 'image/*',
    });
  };

  const type = prop.getType();
  const defValue = prop.getDefaultValue();
  const canClear = prop.canClear();
  const hasValue = prop.hasValue();
  const value = prop.getValue();
  const valueString = hasValue ? value : '';
  const valueWithDef = hasValue ? value : defValue;

  let inputToRender = (
    <TextField
      placeholder={defValue}
      value={valueString}
      onChange={onChange}
      size="small"
      fullWidth
      {...inputBaseProps}
    />
  );

  switch (type) {
    case 'radio':
      {
        const radioProp = prop;
        inputToRender = (
          <RadioGroup 
            value={value} 
            onChange={onChange} 
            row
            sx={{
              '& .MuiRadio-root': {
                color: 'rgba(0, 0, 0, 0.3)',
                '&.Mui-checked': {
                  color: '#3b82f6',
                },
              },
            }}
          >
            {radioProp.getOptions().map((option) => (
              <FormControlLabel
                key={radioProp.getOptionId(option)}
                value={radioProp.getOptionId(option)}
                label={<span className="text-sm text-gray-700">{radioProp.getOptionLabel(option)}</span>}
                control={<Radio size="small" />}
              />
            ))}
          </RadioGroup>
        );
      }
      break;
    case 'select':
      {
        const selectProp = prop;
        inputToRender = (
          <FormControl fullWidth size="small">
            <Select 
              value={value} 
              onChange={onChange}
              {...inputBaseProps}
            >
              {selectProp.getOptions().map((option) => (
                <MenuItem
                  key={selectProp.getOptionId(option)}
                  value={selectProp.getOptionId(option)}
                >
                  {selectProp.getOptionLabel(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      break;
    case 'color':
      {
        inputToRender = (
          <TextField
            fullWidth
            placeholder={defValue}
            value={valueString}
            onChange={onChange}
            size="small"
            {...inputBaseProps}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <div className="w-[24px] h-[24px] rounded-full border border-gray-200/50 overflow-hidden">
                    <div
                      className="w-full h-full"
                      style={{ backgroundColor: valueWithDef }}
                    >
                      <input
                        type="color"
                        className="w-full h-full cursor-pointer opacity-0"
                        value={valueWithDef}
                        onChange={(ev) => handleChange(ev.target.value)}
                      />
                    </div>
                  </div>
                </InputAdornment>
              ),
            }}
          />
        );
      }
      break;
    case 'slider':
      {
        const sliderProp = prop;
        inputToRender = (
          <Slider
            size="small"
            value={parseFloat(value)}
            min={sliderProp.getMin()}
            max={sliderProp.getMax()}
            step={sliderProp.getStep()}
            onChange={onChange}
            valueLabelDisplay="auto"
            sx={{
              color: '#3b82f6',
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20,
                backgroundColor: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                },
              },
              '& .MuiSlider-rail': {
                opacity: 0.3,
              },
            }}
          />
        );
      }
      break;
    case 'file':
      {
        inputToRender = (
          <div className="flex flex-col items-center gap-3">
            {value && value !== defValue && (
              <div
                className="w-[80px] h-[80px] rounded-2xl border border-gray-200/50 bg-cover bg-center cursor-pointer overflow-hidden shadow-sm hover:shadow transition-shadow duration-150"
                style={{ backgroundImage: `url("${value}")` }}
                onClick={() => handleChange('')}
              />
            )}
            <button 
              type="button" 
              onClick={openAssets}
              className="px-4 py-2 bg-blue-500 text-white rounded-full text-sm font-medium hover:bg-blue-600 active:bg-blue-700 transition-colors duration-150"
            >
              Select Image
            </button>
          </div>
        );
      }
      break;
    case 'composite':
      {
        const compositeProp = prop;
        inputToRender = (
          <div className="flex flex-wrap p-3 bg-white/30 backdrop-blur-sm rounded-xl border border-gray-100/50">
            {compositeProp.getProperties().map((prop) => (
              <StylePropertyField key={prop.getId()} prop={prop} />
            ))}
          </div>
        );
      }
      break;
    case 'stack':
      {
        const stackProp = prop;
        const layers = stackProp.getLayers();
        const isTextShadow = stackProp.getName() === 'text-shadow';
        inputToRender = (
          <div className="flex flex-col p-3 gap-3 bg-white/30 backdrop-blur-sm rounded-xl border border-gray-100/50 min-h-[54px]">
            {layers.map((layer) => (
              <div key={layer.getId()} className="bg-white/50 rounded-xl overflow-hidden">
                <div className="flex gap-2 px-3 py-2 items-center border-b border-gray-100/50">
                  <IconButton
                    size="small"
                    onClick={() => layer.move(layer.getIndex() - 1)}
                    className="hover:bg-black/5"
                  >
                    <KeyboardArrowUp className="w-4 h-4 text-gray-500" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => layer.move(layer.getIndex() + 1)}
                    className="hover:bg-black/5"
                  >
                    <KeyboardArrowDown className="w-4 h-4 text-gray-500" />
                  </IconButton>
                  <div className="ml-auto flex gap-1">
                    {canClear && (
                      <IconButton
                        size="small"
                        onClick={() => layer.resetValue()}
                        className="hover:bg-black/5"
                      >
                        <Close className="w-4 h-4 text-gray-500" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => layer.remove()}
                      className="hover:bg-black/5"
                    >
                      <Delete className="w-4 h-4 text-gray-500" />
                    </IconButton>
                  </div>
                </div>
                <div className="p-3">
                  {layer.getProperties().map((prop) => (
                    <StylePropertyField key={prop.getId()} prop={prop} />
                  ))}
                </div>
              </div>
            ))}
            <button
              className="self-start px-3 py-1.5 bg-blue-500 text-white rounded-full text-sm font-medium hover:bg-blue-600 active:bg-blue-700 transition-colors duration-150 flex items-center gap-1"
              onClick={() => stackProp.addLayer({}, { at: 0 })}
            >
              <Add className="w-4 h-4" />
              Add {isTextShadow ? 'Shadow' : 'Layer'}
            </button>
          </div>
        );
      }
      break;
  }

  return (
    <div className="mb-4 w-full">
      <label className="block text-sm font-medium text-gray-600 mb-1.5">
        {prop.getLabel()}
      </label>
      {inputToRender}
    </div>
  );
}
